/**
 * Backend replay failed — say so kindly, then fix it without being asked.
 *
 * Career pages replay a learner's latest completed run from the backend. When
 * that fetch fails the page itself is still useful (the career is right there),
 * so this is a notice and not an error screen. We auto-retry with a visible
 * countdown, and always leave a button so nobody has to wait for us.
 *
 * Presentation only — the caller owns the fetch.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, WifiOff } from "lucide-react";

/** Auto-retry waits, in seconds. After the last one we stop and wait for a tap. */
const BACKOFF_SECONDS = [10, 30, 60];

interface Props {
  /** How many times the replay has already failed. Drives the backoff. */
  attempt: number;
  /** True while a retry is in flight. */
  retrying: boolean;
  /** True when this device still has usable answers to fall back on. */
  hasLocalFallback: boolean;
  /** Ask the caller to fetch again. */
  onRetry: () => void;
}

export function ReplayRetryNotice({ attempt, retrying, hasLocalFallback, onRetry }: Props) {
  const wait = BACKOFF_SECONDS[attempt - 1] ?? null;
  const [remaining, setRemaining] = useState<number | null>(wait);
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  // One countdown per failure. When it reaches zero we retry ourselves.
  useEffect(() => {
    if (wait == null || retrying) { setRemaining(wait); return; }
    setRemaining(wait);
    let left = wait;
    const t = window.setInterval(() => {
      left -= 1;
      if (left <= 0) {
        window.clearInterval(t);
        setRemaining(0);
        onRetryRef.current();
      } else {
        setRemaining(left);
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [attempt, wait, retrying]);

  const body = hasLocalFallback
    ? "We couldn't reach your saved results, so the signals below come from the run on this device. They may be a little behind."
    : "We couldn't reach your saved results, so this page is showing the career on its own — without your personal match yet.";

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="replay-retry-notice"
      className="mt-3 rounded-2xl border border-border bg-card/80 px-4 py-3
        flex flex-wrap items-center gap-x-3 gap-y-2"
    >
      <WifiOff className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm text-foreground/90 flex-1 min-w-[14rem]">
        {body}{" "}
        <span className="text-muted-foreground">
          {retrying
            ? "Trying again now…"
            : remaining && remaining > 0
              ? `Trying again in ${remaining}s.`
              : "We'll stop trying on our own — tap below when you're ready."}
        </span>
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        data-testid="replay-retry-button"
        className="min-h-[44px] px-4 rounded-xl border border-primary/40 bg-primary/10 text-primary
          text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {retrying
          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
        {retrying ? "Retrying" : "Retry now"}
      </button>
    </div>
  );
}
